<script lang="ts">
  /**
   * Зведений екран усіх коментарів клубу.
   *
   * Окремим роутом, а не третім підтабом усередині AdminDashboardPage: той файл
   * уже під 5000 рядків, і повноцінний екран із власними фільтрами й модалками
   * зробив би його некерованим. Прецедент у проєкті вже є — «Оплати»,
   * «Активність» і «Реферали» теж живуть окремими сторінками й повертаються
   * назад через adminState.
   */
  import { onMount } from 'svelte';
  import { replace, pop } from 'svelte-spa-router';
  import Avatar from '../components/Avatar.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';
  import Icon from '../components/Icon.svelte';
  import { session } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import { formatDateTime } from '../lib/format';
  import { COMMENT_MAX_LENGTH, commentReportLabel } from '../lib/types';
  import type { AdminCommentDTO, AdminCommentStatus } from '../lib/types';

  const PAGE_SIZE = 30;

  const STATUS_TABS: { value: AdminCommentStatus; label: string }[] = [
    { value: 'all', label: 'Усі' },
    { value: 'reported', label: 'Зі скаргами' },
    { value: 'deleted', label: 'Видалені' },
  ];

  let items = $state<AdminCommentDTO[]>([]);
  let total = $state(0);
  let page = $state(1);
  let hasMore = $state(false);
  let reportedPending = $state(0);

  let loading = $state(true);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);
  let busyId = $state<string | null>(null);
  let expandedId = $state<string | null>(null);

  let searchInput = $state(adminState.commentsSearch);
  let status = $state<AdminCommentStatus>(adminState.commentsStatus);
  let materialId = $state(adminState.commentsMaterialId);

  let deleteTarget = $state<AdminCommentDTO | null>(null);
  let dismissTarget = $state<AdminCommentDTO | null>(null);

  /** Відкрита форма відповіді — одна на екран, щоб не губити недописане. */
  let replyingId = $state<string | null>(null);
  let replyText = $state('');
  let replyError = $state<string | null>(null);

  // Пишемо назад у модульний кеш — повернення з матеріалу не має скидати фільтр.
  $effect(() => {
    adminState.commentsSearch = searchInput;
    adminState.commentsStatus = status;
    adminState.commentsMaterialId = materialId;
  });

  /** Назва запіненого матеріалу — беремо з першого рядка, окремий запит зайвий. */
  const pinnedTitle = $derived(materialId ? (items[0]?.material?.title ?? null) : null);

  function goBack(): void {
    adminState.activeTab = 'content';
    if (window.history.length > 1) pop();
    else replace('/admin/dashboard');
  }

  async function load(opts: { append?: boolean } = {}): Promise<void> {
    if (opts.append) loadingMore = true;
    else loading = true;
    error = null;
    try {
      const nextPage = opts.append ? page + 1 : 1;
      const res = await api.adminComments({
        search: searchInput.trim() || undefined,
        materialId: materialId || undefined,
        status,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      items = opts.append ? [...items, ...res.items] : res.items;
      page = res.page;
      total = res.total;
      hasMore = res.hasMore;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        replace('/home');
        return;
      }
      error = 'Не вдалось завантажити коментарі';
    } finally {
      loading = false;
      loadingMore = false;
    }
  }

  async function loadStats(): Promise<void> {
    try {
      reportedPending = (await api.adminCommentStats()).reportedPending;
    } catch {
      /* бейдж — не критичний, мовчки лишаємо старе число */
    }
  }

  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function onSearchChange(e: Event): void {
    searchInput = (e.target as HTMLInputElement).value;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => load(), 300);
  }

  function setStatus(next: AdminCommentStatus): void {
    if (status === next) return;
    status = next;
    load();
  }

  function unpinMaterial(): void {
    materialId = '';
    load();
  }

  onMount(() => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    adminMode.set('admin');
    load();
    loadStats();
  });

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    const target = deleteTarget;
    busyId = target.id;
    try {
      await api.deleteComment(target.id);
      // Видалення закриває і скаргу — рядок більше не в черзі модерації.
      if (target.reportCount > 0 && !target.reviewedAt) {
        reportedPending = Math.max(0, reportedPending - 1);
      }
      if (status === 'all' || status === 'reported') {
        items = items.filter((c) => c.id !== target.id);
        total = Math.max(0, total - 1);
      } else {
        await load();
      }
    } catch {
      error = 'Не вдалось видалити коментар';
    } finally {
      busyId = null;
      deleteTarget = null;
    }
  }

  async function confirmDismiss(): Promise<void> {
    if (!dismissTarget) return;
    const target = dismissTarget;
    busyId = target.id;
    try {
      await api.adminDismissCommentReports(target.id);
      reportedPending = Math.max(0, reportedPending - 1);
      if (status === 'reported') {
        items = items.filter((c) => c.id !== target.id);
        total = Math.max(0, total - 1);
      } else {
        const row = items.find((c) => c.id === target.id);
        if (row) row.reviewedAt = new Date().toISOString();
      }
    } catch {
      error = 'Не вдалось відхилити скаргу';
    } finally {
      busyId = null;
      dismissTarget = null;
    }
  }

  function openReply(c: AdminCommentDTO): void {
    replyingId = c.id;
    replyText = '';
    replyError = null;
  }

  async function sendReply(target: AdminCommentDTO): Promise<void> {
    const text = replyText.trim();
    if (!text) return;
    busyId = target.id;
    replyError = null;
    try {
      const created = await api.adminReplyComment(target.id, text);
      replyingId = null;
      replyText = '';
      // У «Усіх» показуємо відповідь одразу під тим, на що відповідали. У черзі
      // скарг їй не місце — там лише заскаржене.
      if (status === 'all') {
        const at = items.findIndex((c) => c.id === target.id);
        items = [...items.slice(0, at + 1), created, ...items.slice(at + 1)];
        total += 1;
      }
    } catch {
      replyError = 'Не вдалось надіслати відповідь';
    } finally {
      busyId = null;
    }
  }

  /** Скарги чекають на розгляд — саме це підсвічує рядок. */
  function isPending(c: AdminCommentDTO): boolean {
    return c.reportCount > 0 && !c.reviewedAt && !c.deletedAt;
  }
</script>

<div class="screen">
  <div class="admin-header">
    <button type="button" class="back-link" onclick={goBack}>← Назад</button>
    <div class="header-title">Коментарі</div>
    <a href="#/profile" class="admin-exit">Вийти</a>
  </div>

  <div class="scroll">
    {#if materialId}
      <div class="pin">
        <span>Лише матеріал: {pinnedTitle ?? '—'}</span>
        <button type="button" onclick={unpinMaterial}>Показати всі <Icon name="x" size="xs" /></button>
      </div>
    {/if}

    <input
      class="search"
      type="search"
      placeholder="Пошук по тексту або автору…"
      value={searchInput}
      oninput={onSearchChange}
    />

    <div class="tabs">
      {#each STATUS_TABS as tab (tab.value)}
        <button
          type="button"
          class="tab"
          class:active={status === tab.value}
          onclick={() => setStatus(tab.value)}
        >
          {tab.label}{#if tab.value === 'reported' && reportedPending > 0}<span class="badge">{reportedPending}</span>{/if}
        </button>
      {/each}
    </div>

    {#if error}
      <div class="placeholder err">{error}</div>
    {/if}

    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if items.length === 0}
      <div class="placeholder">
        {status === 'reported' ? 'Скарг немає — усе чисто.' : 'Коментарів не знайдено'}
      </div>
    {:else}
      <div class="count">Всього: {total}</div>

      {#each items as c (c.id)}
        <div class="row" class:pending={isPending(c)} class:removed={!!c.deletedAt}>
          <div class="row-head">
            <Avatar photo={c.author.photoUrl} name={c.author.fullName} size={32} />
            <div class="who">
              <div class="who-name">
                {c.author.fullName}{#if c.author.isAdmin}<span class="team">Команда</span>{/if}
              </div>
              <div class="who-meta">{formatDateTime(c.createdAt)}</div>
            </div>
            {#if isPending(c)}
              <span class="flag">
                <Icon name="flag" size="xs" />
                {commentReportLabel(c.reports[0]?.reason ?? null)}{#if c.reportCount > 1}&nbsp;· {c.reportCount}{/if}
              </span>
            {/if}
          </div>

          {#if c.material && !materialId}
            <a class="material" href={`#/admin/material/${c.material.id}/edit`}>
              {c.material.title}
            </a>
          {/if}

          {#if c.parent}
            <div class="quote"><b>{c.parent.authorName}:</b> {c.parent.text}</div>
          {/if}

          <div class="text">{c.text}</div>

          <div class="row-meta">
            {#if c.parentId}<span class="chip">відповідь</span>{/if}
            {#if c.likeCount > 0}<span class="chip"><Icon name="heart" size="xs" /> {c.likeCount}</span>{/if}
            {#if c.deletedAt}
              <span class="chip">
                Видалено {formatDateTime(c.deletedAt)}
                {c.deletedBy === 'admin'
                  ? '(адмін)'
                  : c.deletedBy === 'author'
                    ? '(автор)'
                    : c.deletedBy === 'thread'
                      ? '(разом із гілкою)'
                      : ''}
              </span>
            {/if}
            {#if c.reportCount > 0 && c.reviewedAt && !c.deletedAt}
              <span class="chip">Скаргу розглянуто</span>
            {/if}
          </div>

          {#if c.reports.length > 0}
            <button
              type="button"
              class="reports-toggle"
              onclick={() => (expandedId = expandedId === c.id ? null : c.id)}
            >
              {expandedId === c.id ? 'Сховати скарги' : `Скарги (${c.reportCount})`}
            </button>
            {#if expandedId === c.id}
              <div class="reports">
                {#each c.reports as r (r.userId)}
                  <div class="report">
                    <span class="report-who">{r.fullName}</span>
                    <span class="report-date">{formatDateTime(r.createdAt)}</span>
                    <div class="report-reason">{commentReportLabel(r.reason)}</div>
                  </div>
                {/each}
                {#if c.reportCount > c.reports.length}
                  <div class="report-more">…та ще {c.reportCount - c.reports.length}</div>
                {/if}
              </div>
            {/if}
          {/if}

          {#if !c.deletedAt}
            <div class="row-actions">
              {#if isPending(c)}
                <button
                  type="button"
                  class="act"
                  disabled={busyId === c.id}
                  onclick={() => (dismissTarget = c)}
                >Відхилити скаргу</button>
              {/if}
              {#if replyingId !== c.id}
                <button
                  type="button"
                  class="act"
                  disabled={busyId === c.id}
                  onclick={() => openReply(c)}
                >Відповісти</button>
              {/if}
              <button
                type="button"
                class="act danger"
                disabled={busyId === c.id}
                onclick={() => (deleteTarget = c)}
              ><Icon name="trash-2" size="xs" /> Видалити</button>
            </div>

            {#if replyingId === c.id}
              <div class="reply">
                <!-- svelte-ignore a11y_autofocus -->
                <textarea
                  rows="3"
                  maxlength={COMMENT_MAX_LENGTH}
                  placeholder={`Відповідь для ${c.author.fullName}…`}
                  bind:value={replyText}
                  autofocus
                ></textarea>
                {#if replyError}<div class="reply-err">{replyError}</div>{/if}
                <div class="row-actions">
                  <button
                    type="button"
                    class="act primary"
                    disabled={busyId === c.id || !replyText.trim()}
                    onclick={() => sendReply(c)}
                  >{busyId === c.id ? 'Надсилаємо…' : 'Надіслати'}</button>
                  <button type="button" class="act" disabled={busyId === c.id} onclick={() => (replyingId = null)}
                    >Скасувати</button>
                </div>
              </div>
            {/if}
          {/if}
        </div>
      {/each}

      {#if hasMore}
        <button type="button" class="more" disabled={loadingMore} onclick={() => load({ append: true })}>
          {loadingMore ? 'Завантажуємо…' : 'Завантажити ще'}
        </button>
      {/if}
    {/if}
  </div>
</div>

<ConfirmModal
  open={deleteTarget !== null}
  title="Видалити коментар?"
  message={deleteTarget && !deleteTarget.parentId
    ? 'Коментар зникне з клубу разом із відповідями на нього. У списку «Видалені» він лишиться.'
    : 'Коментар зникне з клубу. У списку «Видалені» він лишиться.'}
  confirmLabel="Так, видалити"
  onConfirm={confirmDelete}
  onCancel={() => (deleteTarget = null)}
/>

<ConfirmModal
  open={dismissTarget !== null}
  title="Відхилити скаргу?"
  message="Коментар лишиться в клубі, а позначка «потребує уваги» згасне. Історія скарг збережеться."
  confirmLabel="Відхилити"
  danger={false}
  onConfirm={confirmDismiss}
  onCancel={() => (dismissTarget = null)}
/>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link {
    font-size: 12px;
    color: rgba(251, 246, 236, 0.9);
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 4px 0;
  }
  .header-title {
    flex: 1;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
  }
  .admin-exit {
    font-size: 11px;
    opacity: 0.85;
    background: rgba(251, 246, 236, 0.1);
    padding: 5px 11px;
    border-radius: 7px;
    color: var(--text-light);
    text-decoration: none;
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 18px 60px;
  }

  .pin {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: var(--sand-soft);
    border-radius: 10px;
    padding: 9px 12px;
    margin-bottom: 12px;
    font-size: 12px;
    color: var(--text-muted);
  }
  .pin button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 12px;
    color: var(--pu);
    cursor: pointer;
    flex-shrink: 0;
  }

  .search {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--bg2);
    color: var(--text);
    font-family: inherit;
    font-size: 13px;
    outline: none;
  }
  .tabs {
    display: flex;
    gap: 6px;
    margin: 12px 0;
  }
  .tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--bg2);
    font-family: inherit;
    font-size: 12px;
    color: var(--text-muted);
    cursor: pointer;
  }
  .tab.active {
    background: var(--dark);
    border-color: var(--dark);
    color: var(--text-light);
  }
  .badge {
    background: var(--heart);
    color: var(--text-light);
    border-radius: 999px;
    min-width: 17px;
    padding: 1px 5px;
    font-size: 10px;
    font-weight: 700;
    text-align: center;
  }

  .count {
    font-size: 11px;
    color: var(--faint);
    margin-bottom: 8px;
  }

  .row {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 10px;
  }
  /* Скарга, яку ще не розглянули, має впадати в око з першого погляду. */
  .row.pending {
    border-left: 3px solid var(--heart);
    background: var(--pu-light);
  }
  .row.removed {
    opacity: 0.6;
  }
  .row-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .who {
    flex: 1;
    min-width: 0;
  }
  .who-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .who-meta {
    font-size: 11px;
    color: var(--faint);
  }
  .flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 55%;
    text-align: right;
    background: var(--heart);
    color: var(--text-light);
    border-radius: 999px;
    padding: 3px 9px;
    font-size: 11px;
    font-weight: 600;
  }
  .material {
    display: inline-block;
    margin-top: 8px;
    font-size: 11px;
    color: var(--pu);
    text-decoration: none;
  }
  .text {
    margin-top: 8px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--text);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .row-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--sand-soft);
    border-radius: 6px;
    padding: 3px 7px;
    font-size: 11px;
    color: var(--text-muted);
  }
  .reports-toggle {
    margin-top: 8px;
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;
    font-size: 12px;
    color: var(--pu);
    cursor: pointer;
  }
  .reports {
    margin-top: 8px;
    border-top: 1px solid var(--border);
    padding-top: 8px;
  }
  .report {
    margin-bottom: 6px;
  }
  .report-who {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }
  .report-date {
    font-size: 11px;
    color: var(--faint);
    margin-left: 6px;
  }
  .report-reason {
    font-size: 12px;
    color: var(--text-muted);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .report-more {
    font-size: 11px;
    color: var(--faint);
  }
  .row-actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
  .act {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 12px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--bg);
    font-family: inherit;
    font-size: 12px;
    color: var(--text);
    cursor: pointer;
  }
  .act.danger {
    color: var(--status-inactive);
    border-color: var(--border2);
  }
  .act.primary {
    background: var(--pu);
    border-color: var(--pu);
    color: var(--on-pu);
  }
  .team {
    margin-left: 6px;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--sage-light);
    color: var(--sage-ink);
  }
  .quote {
    margin-top: 8px;
    padding: 6px 10px;
    border-left: 2px solid var(--border2);
    font-size: 12px;
    color: var(--text-muted);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
  .reply {
    margin-top: 10px;
  }
  .reply textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text);
    font-family: inherit;
    font-size: 16px; /* менше — і iOS зумить сторінку на фокусі */
    resize: vertical;
  }
  .reply-err {
    margin-top: 6px;
    font-size: 12px;
    color: var(--status-inactive);
  }
  .act:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .placeholder {
    padding: 16px;
    text-align: center;
    color: var(--text-muted);
    font-size: 12px;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 10px;
  }
  .placeholder.err {
    color: var(--status-inactive);
    margin-bottom: 14px;
  }
  .more {
    width: 100%;
    margin-top: 6px;
    padding: 10px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    font-family: inherit;
    font-size: 13px;
    color: var(--text);
    cursor: pointer;
  }
  .more:disabled {
    opacity: 0.6;
    cursor: default;
  }
</style>
